import { useEffect, useMemo, useState } from 'react';
import type { Modality } from './types';
import { fetchAggregate } from './lib/api';
import { useLabStore } from './store/useLabStore';
import { Experiment } from './components/Experiment';
import { CommonsMap } from './components/CommonsMap';
import { ResultsPanel } from './components/ResultsPanel';

const LINKEDIN_URL = 'https://www.linkedin.com/in/mindful-abhay/';
const COFFEE_URL = 'https://buymeacoffee.com/abhaypratapsingh';

const MODES: Array<{ id: Modality; title: string; one: string; phenomenon: string }> = [
  { id: 'color', title: 'Color', one: 'Find the smallest color drift your eyes can still catch.', phenomenon: 'Just-noticeable difference, modeled in Oklab color distance.' },
  { id: 'contrast', title: 'Contrast', one: 'Hunt a ring that almost dissolves into darkness.', phenomenon: 'Contrast detection threshold under two-alternative forced choice.' },
  { id: 'pitch', title: 'Pitch', one: 'Hear the smallest upward bend between two tones.', phenomenon: 'Frequency difference limen, reported in musical cents.' }
];

export function App() {
  const [selected, setSelected] = useState<Modality>(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    return mode === 'contrast' || mode === 'pitch' || mode === 'color' ? mode : 'color';
  });
  const { aggregate, setAggregate, lastResult } = useLabStore();

  useEffect(() => {
    fetchAggregate().then(setAggregate).catch(() => setAggregate(null));
  }, [setAggregate]);

  const selectedMode = useMemo(() => MODES.find((mode) => mode.id === selected) ?? MODES[0], [selected]);

  return (
    <main className="app-shell">
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">Limen Lab · psychophysics as a public toy</p>
          <h1>Find the exact edge of perception.</h1>
          <p className="lede">
            A beautiful little forced-choice instrument for color, contrast, and pitch. The browser runs the staircase; Cloudflare only keeps the anonymous commons.
          </p>
          <div className="hero-badges">
            <span>2AFC</span>
            <span>QUEST-ish Bayesian posterior</span>
            <span>device-keyed</span>
            <span>Turnstile-gated</span>
          </div>
          <div className="creator-actions" aria-label="Creator links">
            <a className="support-link" href={COFFEE_URL} target="_blank" rel="noreferrer">Buy me a coffee</a>
            <a className="social-link" href={LINKEDIN_URL} target="_blank" rel="noreferrer">Connect on LinkedIn</a>
          </div>
        </div>
        <div className="hero-orb" aria-hidden="true">
          <span />
        </div>
      </section>

      <section className="mode-strip" aria-label="Choose a threshold task">
        {MODES.map((mode) => (
          <button key={mode.id} className={`mode-card ${selected === mode.id ? 'active' : ''}`} onClick={() => setSelected(mode.id)}>
            <span>{mode.title}</span>
            <strong>{mode.one}</strong>
            <small>{mode.phenomenon}</small>
          </button>
        ))}
      </section>

      <div className="science-note">
        <strong>{selectedMode.title} edge:</strong> {selectedMode.phenomenon} This is not a clinical measurement; displays, headphones, fatigue, and room light all become part of the signal.
      </div>

      <div className="workspace-grid">
        <Experiment selected={selected} />
        <div className="side-stack">
          <ResultsPanel aggregate={aggregate} selected={selected} />
          <CommonsMap aggregate={aggregate} selected={selected} personalThreshold={lastResult?.modality === selected ? lastResult.threshold : undefined} />
        </div>
      </div>

      <footer className="app-footer">
        <p>
          Built by <a href={LINKEDIN_URL} target="_blank" rel="noreferrer">Mindful Abhay</a> for Cloudflare’s free tier: Pages, a tiny Pages Function, D1, and Turnstile. Support future experiments at <a href={COFFEE_URL} target="_blank" rel="noreferrer">Buy Me a Coffee</a>.
        </p>
      </footer>
    </main>
  );
}
